import { CalendarCheckIcon, PencilSimpleIcon, PlusIcon, ProhibitIcon } from '@phosphor-icons/react'
import { type ReactNode, useCallback, useEffect, useState } from 'react'
import { ListBoard } from 'app/components/lists/board/ListBoard'
import { ListBoardExit } from 'app/components/lists/board/ListBoardExit'
import type { ListBoardLaneDef, ListBoardSelection } from 'app/components/lists/board/boardTypes'
import { useListStageModals } from 'app/components/lists/board/useListStageModals'
import { useStageVisibility } from 'app/components/lists/manual/useStageVisibility'
import { SystemListSetupCard } from 'app/components/lists/system/SystemListSetupCard'
import { Button } from 'app/components/ui/button'
import type { List } from 'app/generated/graphql'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { closeModal, showModal } from 'app/lib/modal'
import type { TreatmentLane } from 'app/types/treatment'
import { TREATMENT_LIST } from 'lib/systemLists'
import { CaseCard } from './CaseCard'
import { ExitCaseList } from './ExitCaseList'
import {
  EXIT_STAGE_MODAL_OPTIONS,
  ExitStageCount,
  ExitStageListBody,
  exitStageSearch,
  useContactSearchCount,
} from './ExitStage'
import { bigMoney } from './treatmentDisplay'
import { TreatmentCard } from './treatmentFixture'
import { BoardSort, TREATMENT_PIPELINE_ID, useTreatmentBoard } from './useTreatmentBoard'

const TREATMENT_EXITS = [
  {
    id: `scheduled` as const,
    // The stage this exit writes, which is what the count is read against.
    stage: `Scheduled`,
    icon: CalendarCheckIcon,
    badge: `green` as const,
    resting: `Drag a case here once it is booked`,
    dropping: `Drop to mark it scheduled`,
    armed: `border-green-500 bg-green-50/70 text-green-900`,
    over: `border-solid border-green-600 bg-green-100 text-green-900 shadow-elevation-2`,
  },
  {
    id: `lost` as const,
    stage: `Lost`,
    icon: ProhibitIcon,
    // Grey, not red. A case the practice lost is a fact to record, not an
    // error to alarm anybody about, and the count sits on a resting board.
    badge: `gray` as const,
    resting: `Drag a case here if it is lost`,
    dropping: `Drop to mark it lost`,
    armed: `border-red-300 bg-red-50/70 text-red-900`,
    over: `border-solid border-red-500 bg-red-100 text-red-900 shadow-elevation-2`,
  },
]

type TreatmentExitDef = (typeof TREATMENT_EXITS)[number]

/** An exit and the search that finds who is at it: pipeline membership on the old board, the status on the list. */
type ExitTarget = { exit: TreatmentExitDef; search: string }

/**
 * One exit zone: a drop target at work, and a way into the stage at rest. Its count's query lives
 * here because each zone reads a different stage, and a hook cannot be called inside the map that
 * draws them.
 */
function TreatmentExit({
  exit,
  search,
  draggingId,
  writes,
  onDrop,
  onOpenList,
}: ExitTarget & {
  draggingId?: string
  /** Bumped by the board on every move that lands, so the count re-reads. */
  writes: number
  onDrop: (cardId: string) => void
  onOpenList: () => void
}) {
  const count = useContactSearchCount(search, writes)
  return (
    <ListBoardExit
      icon={exit.icon}
      resting={exit.resting}
      dropping={exit.dropping}
      armedClass={exit.armed}
      overClass={exit.over}
      armed={!!draggingId}
      fallbackId={draggingId}
      onDrop={onDrop}
      onOpenList={onOpenList}
      title={`Show ${count ? `the ${count} ` : `everyone `}marked ${exit.stage.toLowerCase()}`}
    >
      {/* A case dropped here leaves the board (its rollup soon stops matching
          the band), so without this count booked cases vanish from the board
          that booked them. Pressing the zone lists them. */}
      <ExitStageCount count={count} label={exit.stage} color={exit.badge} />
    </ListBoardExit>
  )
}

// Mounted only while a list is open, so its count is read only then.
function ExitListModal({
  target,
  sort,
  writes,
  onOpenCase,
  onClose,
}: {
  target: ExitTarget
  sort: BoardSort
  writes: number
  onOpenCase: (card: TreatmentCard) => void
  onClose: () => void
}) {
  const count = useContactSearchCount(target.search, writes)
  // Through the app's modal system; ExitCaseList fetches its own cases inside
  // the modal tree, so only the count needs a re-show when it lands.
  useEffect(() => {
    showModal(
      <ExitStageListBody count={count ?? 0}>
        <ExitCaseList
          stage={target.exit.stage}
          search={target.search}
          sort={sort}
          onOpen={(card) => {
            onClose()
            onOpenCase(card)
          }}
        />
      </ExitStageListBody>,
      {
        ...EXIT_STAGE_MODAL_OPTIONS,
        title: `${target.exit.stage} · ${count ?? 0}`,
        onClose,
      },
      false,
    )
  }, [target, count, sort, onOpenCase, onClose])
  return null
}

type StatusEditing = {
  headerAction?: (lane: TreatmentLane) => ReactNode
  afterLanes?: ReactNode
  /** The stages this person shows, in their order. Absent on the board that is not on Lists. */
  visibleStageIds?: readonly string[]
}

// Its own component so the stage modals, which need a loaded list, are never a conditional hook.
function ListStatusEditing({
  list,
  onChanged,
  children,
}: {
  list: List
  onChanged: () => void
  children: (editing: StatusEditing) => ReactNode
}) {
  const { openAddStage, openStageEditor } = useListStageModals({ list, onChanged })
  const canEditStatuses = useCheckPermission()([`org:pages:pipelines`, `pages:pipelines`])
  const { visibleStages } = useStageVisibility(list)
  const stageById = new Map((list.stages ?? []).map((stage) => [stage.id, stage]))
  return (
    <>
      <SystemListSetupCard def={TREATMENT_LIST} list={list} onBuildOwn={openAddStage} onChanged={onChanged} />
      {children({
        visibleStageIds: visibleStages.flatMap((stage) => (stage.id ? [stage.id] : [])),
        // `system` is null, not false, on a stage the workspace added.
        headerAction: (lane) => {
          const stage = stageById.get(lane.id)
          return !stage || stage.system || !canEditStatuses ? undefined : (
            <Button
              variant="icon"
              size="icon-sm"
              aria-label={`Edit ${stage.name}`}
              onClick={() => openStageEditor({ ...stage, visible: true })}
            >
              <PencilSimpleIcon size={14} weight="bold" />
            </Button>
          )
        },
        afterLanes: canEditStatuses && (
          <Button variant="ghost" className="mt-1 shrink-0 self-start" onClick={openAddStage}>
            <PlusIcon size={16} weight="bold" /> Add stage
          </Button>
        ),
      })}
    </>
  )
}

const FIRST_STAGE_SKELETONS = 3
const OTHER_STAGE_SKELETONS = 1

function CaseSkeleton() {
  return (
    <div className="shrink-0 animate-pulse rounded-xl border border-default bg-surface p-3.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="h-3.5 w-2/5 rounded bg-surface-muted" />
          <div className="mt-1.5 h-2.5 w-1/4 rounded bg-surface-muted" />
        </div>
        <div className="h-5 w-14 rounded bg-surface-muted" />
      </div>
      <div className="mt-3.5 space-y-1.5">
        <div className="h-2.5 w-3/5 rounded bg-surface-muted" />
        <div className="h-2.5 w-1/2 rounded bg-surface-muted" />
      </div>
    </div>
  )
}

/**
 * The four working stages as columns, with the two exits beneath them.
 *
 * Cases worth thousands take several conversations, and the job this view is
 * for is holding a handful of them in flight at once. The table next door
 * (`TreatmentTable`) reads the same band for the opposite job: seeing all of
 * it at once.
 */
export function TreatmentColumns({
  board,
  sort,
  onOpenCase,
  selection,
}: {
  board: ReturnType<typeof useTreatmentBoard>
  sort: BoardSort
  onOpenCase: (card: TreatmentCard) => void
  /** Set while the board is picking cases for a bulk action. See `ListBoard`. */
  selection?: ListBoardSelection
}) {
  const [exitList, setExitList] = useState<ExitTarget | undefined>(undefined)
  const closeExitList = useCallback(() => setExitList(undefined), [])
  useEffect(() => {
    if (!exitList) closeModal()
  }, [exitList])

  // The old board draws both exits; the list draws only the ones it has a status for.
  const exitTargets: ExitTarget[] = board.listBacked
    ? TREATMENT_EXITS.filter((exit) => board.exits.some((listExit) => listExit.label === exit.stage)).map((exit) => ({
        exit,
        search: `treatmentStatus.keyword:"${exit.stage}"`,
      }))
    : TREATMENT_EXITS.map((exit) => ({ exit, search: exitStageSearch(TREATMENT_PIPELINE_ID, exit.stage) }))

  // Skeletons only on the first read: a later page arriving must not blank lanes that
  // already have cases in them.
  const firstRead = board.loading && board.sampleCount === 0
  const renderBoard = ({ headerAction, afterLanes, visibleStageIds }: StatusEditing) => {
    const shownColumns = visibleStageIds
      ? visibleStageIds.flatMap((id) => board.columns.filter((column) => column.stage.id === id))
      : board.columns
    const lanes: ListBoardLaneDef<TreatmentCard, string>[] = shownColumns.map((column, index) => {
      const reading = firstRead && column.cards.length === 0
      return {
        key: column.stage.id,
        label: column.stage.label,
        headerAction: headerAction?.(column.stage),
        count: reading ? (
          <span className="block h-3 w-5 animate-pulse rounded bg-surface-muted" />
        ) : (
          <p className="shrink-0 text-body-xs tabular-nums text-subtle">{column.cards.length}</p>
        ),
        headerExtra: (
          <>
            {column.stage.blurb && (
              <p className="mt-0.5 text-body-xs leading-relaxed text-subtle">{column.stage.blurb}</p>
            )}
            {reading ? (
              <span className="mt-1.5 block h-4 w-12 animate-pulse rounded bg-surface-muted" />
            ) : (
              <p className="mt-1.5 gilroy text-base leading-none text-default">{bigMoney(column.value)}</p>
            )}
          </>
        ),
        items: column.cards,
        placeholders:
          reading &&
          Array.from({ length: index === 0 ? FIRST_STAGE_SKELETONS : OTHER_STAGE_SKELETONS }, (_, skeleton) => (
            <CaseSkeleton key={skeleton} />
          )),
        emptyLabel: reading ? undefined : `Nothing here`,
      }
    })

    return (
      // Touch drag stays off: a case card carries no `data-board-card` yet (`liftable={false}`
      // in `CaseCard`), so there is nothing for a finger to lift. Turning it on is a follow-up
      // that starts with the board's parity snapshot, which pins the chrome.
      <ListBoard
        lanes={lanes}
        itemId={(card) => card.id}
        tone="green"
        width="wide"
        selection={selection}
        onMove={(cardId, stageId) => board.move(cardId, stageId)}
        renderCard={(card, drag) => (
          <CaseCard
            card={card}
            dragging={drag.dragging}
            onOpen={() => onOpenCase(card)}
            onDragStart={drag.onDragStart}
            onDragEnd={drag.onDragEnd}
            select={drag.select}
          />
        )}
        afterLanes={afterLanes}
        belowLanes={
          (board.hasMore || (board.loading && board.sampleCount > 0)) && (
            <Button
              variant="subtle"
              onClick={board.loadMore}
              disabled={board.loading}
              className="mt-2 h-auto w-full rounded-xl border-default px-4 py-2.5 text-body-xs font-normal hover:border-strong hover:bg-transparent disabled:text-minimal disabled:opacity-100"
            >
              {board.loading
                ? `Loading more cases`
                : `Load more · ${(board.total - board.sampleCount).toLocaleString(`en-US`)} still to come`}
            </Button>
          )
        }
        // Pressing an exit reads its whole stage in a modal (see `ExitListModal`), rather
        // than spelling out underneath it only the part the board happened to have loaded.
        renderExits={
          exitTargets.length === 0
            ? undefined
            : (drag) =>
                exitTargets.map((target) => (
                  <TreatmentExit
                    key={target.exit.id}
                    exit={target.exit}
                    search={target.search}
                    draggingId={drag.draggingId}
                    writes={board.writes}
                    onOpenList={() => setExitList(target)}
                    onDrop={(cardId) => {
                      const moved = cardId || drag.draggingId
                      if (moved) {
                        if (target.exit.id === `scheduled`) board.markScheduled(moved)
                        else board.decline(moved)
                      }
                      drag.endDrag()
                    }}
                  />
                ))
        }
      />
    )
  }

  return (
    <>
      {exitList && (
        <ExitListModal
          target={exitList}
          sort={sort}
          writes={board.writes}
          onOpenCase={onOpenCase}
          onClose={closeExitList}
        />
      )}
      {board.listBacked && board.list ? (
        <ListStatusEditing
          list={board.list}
          onChanged={() => {
            board.reloadList()
            board.reload()
          }}
        >
          {renderBoard}
        </ListStatusEditing>
      ) : (
        renderBoard({})
      )}
    </>
  )
}
